import { Grid, InputLabel, useTheme } from '@mui/material';
import { AxiosResponse } from 'axios';
import { FILE_TYPE, LABEL_DATA_RASTERS, LayerTypeEnum, mapFileLabel } from 'common/defines/constants';
import { convertTextToMultiLanguage } from 'common/utils/convert';
import { ButtonCustom } from 'components/Common/ButtonCustom';
import SelectFile from 'components/Common/SelectFile';
import { QUERY_KEY } from 'constants/constants';
import { Formik } from 'formik';
import { FC, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import {
  createCo2Sequestration,
  deleteRasterAnalysisData,
  getRasterDetails,
} from 'services/analytics/apiAnalyticsConfig.services';
import { ErrorsDialog } from './ErrorDialog';

export const layerTypeListImages = [
  LayerTypeEnum.COVER_20_LEGEND,
  LayerTypeEnum.CO2_SEQUESTRATION_LEGEND,
  LayerTypeEnum.ABOVEGROUND_CARBON_DENSITY_LEGEND,
];

interface ICo2SequestrationForm {
  sensorId: any;
  deleteFileGeojson: (id: string) => Promise<AxiosResponse<any, any>>;
}

export const Co2SequestrationForm: FC<ICo2SequestrationForm> = ({ sensorId, deleteFileGeojson }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const formRef = useRef<any>();
  const theme = useTheme();

  const layerTypeList = useMemo(
    () => [
      LayerTypeEnum.COVER_20,
      LayerTypeEnum.COVER_20_LEGEND,
      LayerTypeEnum.CO2_SEQUESTRATION,
      LayerTypeEnum.CO2_SEQUESTRATION_LEGEND,
      LayerTypeEnum.ABOVEGROUND_CARBON_DENSITY,
      LayerTypeEnum.ABOVEGROUND_CARBON_DENSITY_LEGEND,
    ],
    []
  );

  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [dataErrors, setDataErrors] = useState(null as any);

  const closeErrorModal = () => {
    setOpenErrorModal(false);
  };

  const { data: analyticDetails, refetch } = useQuery([QUERY_KEY.RASTER_DETAILS, sensorId], () =>
    getRasterDetails(sensorId)
  );

  const createAnalyticMutation = useMutation((data: any) => createCo2Sequestration(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY.ANALYSIS_UPLOAD_HISTORY);
      refetch();
      toast.success('Update Co2 Sequestration Analytic Successful', { toastId: 1 });
    },
    onError: (e: any) => {
      setOpenErrorModal(true);
      setDataErrors(e?.data?.message);
    },
  });

  useEffect(() => {
    formRef.current && formRef.current.resetForm();
  }, [sensorId, formRef]);

  const initialFields = useMemo(() => {
    let initParam: any = {
      mainGeoJsonFile: undefined,
      intervalLimitFile: undefined,
    };
    const uploadHistory = analyticDetails?.data?.uploadHistory;

    if (uploadHistory?.length) {
      uploadHistory.forEach((_item: any) => {
        if (_item.fileType === 'GEO_JSON') {
          if (_item.geoJsonType === 'INTERVAL_LIMIT') {
            initParam.intervalLimitFile = _item;
          } else {
            initParam.mainGeoJsonFile = _item;
          }
        } else {
          if (layerTypeList.includes(_item.layerType)) {
            initParam[_item.layerType] = _item;
          } else {
            initParam[_item.layerType] = undefined;
          }
        }
      });
    }
    return initParam;
  }, [analyticDetails?.data?.uploadHistory, layerTypeList]);

  const getAcceptFile = (layerType: LayerTypeEnum) => {
    if (layerTypeListImages.includes(layerType)) {
      return new RegExp(/\.(png|jpg|tiff)$/);
    } else {
      return new RegExp(/.zip$/);
    }
  };

  return (
    <div>
      <Formik
        innerRef={formRef}
        initialValues={Object.assign(initialFields)}
        enableReinitialize
        onSubmit={(values, { resetForm }) => {
          const formData = new FormData();
          formData.append('analysisId', sensorId);
          formData.append('dataFile', values.mainGeoJsonFile || '');
          formData.append('intervalLimitFile', values.intervalLimitFile || '');
          createAnalyticMutation.mutate(formData, {
            onSuccess: () => {
              resetForm();
            },
          });
        }}>
        {({ values, setFieldValue, handleSubmit, submitForm }) => (
          <form onSubmit={handleSubmit}>
            <Grid display="flex" width="100%">
              <Grid sx={{ maxWidth: '700px', width: '700px' }}>
                <InputLabel sx={{ fontWeight: '600', mt: '24px' }}>Geojson</InputLabel>
                <Grid display="flex" alignItems="center" mt={2} justifyContent="space-between">
                  <InputLabel variant="standard" sx={{ fontSize: '14px', margin: 0 }} htmlFor="uncontrolled-native">
                    {t(convertTextToMultiLanguage(mapFileLabel[FILE_TYPE.CO2_SEQUESTRATION_FILE], 'file_name'))}
                  </InputLabel>
                  <SelectFile
                    acceptFile={new RegExp(/.json$/)}
                    fileSelected={values.mainGeoJsonFile}
                    disabled={createAnalyticMutation.isLoading}
                    onSelectFile={(file) => {
                      setFieldValue('mainGeoJsonFile', file);
                    }}
                    analysisId={sensorId}
                    fileType={FILE_TYPE.CO2_SEQUESTRATION_FILE}
                    deleteFileGeojson={deleteFileGeojson}
                  />
                </Grid>
                <Grid display="flex" alignItems="center" mt={2} justifyContent="space-between">
                  <InputLabel variant="standard" sx={{ fontSize: '14px', margin: 0 }} htmlFor="uncontrolled-native">
                    {t(convertTextToMultiLanguage(mapFileLabel[FILE_TYPE.INTERVALS_FILE], 'file_name'))}
                  </InputLabel>
                  <SelectFile
                    acceptFile={new RegExp(/.json$/)}
                    fileSelected={values.intervalLimitFile}
                    disabled={createAnalyticMutation.isLoading}
                    onSelectFile={(file) => {
                      setFieldValue('intervalLimitFile', file);
                    }}
                    analysisId={sensorId}
                    fileType={FILE_TYPE.INTERVALS_FILE}
                    deleteFileGeojson={deleteFileGeojson}
                  />
                </Grid>

                {/* Raster area  */}
                <InputLabel sx={{ fontWeight: '600', mt: '64px' }}>Raster</InputLabel>
                {layerTypeList.map((layerType, index) => (
                  <Fragment key={index}>
                    <Grid display="flex" alignItems="center" mt={2} justifyContent="space-between">
                      <InputLabel variant="standard" sx={{ fontSize: '14px', margin: 0 }} htmlFor="uncontrolled-native">
                        {LABEL_DATA_RASTERS[layerType]}
                      </InputLabel>
                      <SelectFile
                        acceptFile={getAcceptFile(layerType)}
                        fileSelected={values[layerType]}
                        onSelectFile={(file) => setFieldValue(layerType, file)}
                        analysisId={sensorId}
                        layerType={layerType}
                        deleteFileGeojson={deleteRasterAnalysisData}
                      />
                    </Grid>
                  </Fragment>
                ))}
              </Grid>
              <Grid marginLeft={'auto'}>
                <ButtonCustom
                  title="Save"
                  border="0px"
                  colorLabel="#FFF"
                  sx={{ width: '160px', height: '1.4375em' }}
                  backgroundColor={theme.palette.primary.main}
                  onClick={() => submitForm()}
                  disabled={createAnalyticMutation.isLoading}
                  loading={createAnalyticMutation.isLoading}
                />
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
      <ErrorsDialog
        title="Error Upload"
        open={openErrorModal}
        onClose={closeErrorModal}
        data={dataErrors}
        onCancel={closeErrorModal}
        submit={false}
      />
    </div>
  );
};
